import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { ResponsiveBar } from '@nivo/bar';

import { useUser } from '../../../contexts/UserContext';
import {
  tlsrptApi,
  fmtEpoch,
  fmtNumber,
  fmtPct,
  DomainSummary,
  TimeseriesPoint,
  FailureTypeRow,
  ReporterRow,
  ReportListItem,
} from '../../../services/tlsrptApi';

const ReadinessBadge: React.FC<{ summary: DomainSummary }> = ({ summary }) => {
  if (!summary.last_seen_iso) {
    return <Chip variant="outlined" label="Awaiting first report" />;
  }
  if (summary.enforce_ready) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label={`Ready for enforce (${summary.enforce_ready_window_days}d clean)`}
        color="success"
      />
    );
  }
  if (summary.failures_in_enforce_window > 0) {
    return (
      <Chip
        icon={<ErrorOutlineIcon />}
        label={`${fmtNumber(summary.failures_in_enforce_window)} failures in last ${summary.enforce_ready_window_days}d`}
        color="warning"
      />
    );
  }
  return (
    <Chip
      icon={<HourglassEmptyIcon />}
      label={`Insufficient data (no traffic in last ${summary.enforce_ready_window_days}d)`}
      variant="outlined"
    />
  );
};

const TlsRptDomainDetail: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const { domain: domainParam } = useParams<{ domain: string }>();
  const domain = domainParam ? decodeURIComponent(domainParam) : '';

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<DomainSummary | null>(null);
  const [series, setSeries] = useState<TimeseriesPoint[] | null>(null);
  const [failures, setFailures] = useState<FailureTypeRow[] | null>(null);
  const [reporters, setReporters] = useState<ReporterRow[] | null>(null);
  const [reports, setReports] = useState<ReportListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) navigate('/app/welcome');
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin() || !domain) return;
    setSummary(null);
    setSeries(null);
    setFailures(null);
    setReporters(null);
    tlsrptApi
      .getDomain(domain, days)
      .then(res => {
        setSummary(res.summary);
        setSeries(res.timeseries);
        setFailures(res.top_failure_types);
        setReporters(res.top_reporters);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [domain, days, isAdmin]);

  useEffect(() => {
    if (!isAdmin() || !domain) return;
    tlsrptApi
      .listReports(domain, 50)
      .then(r => setReports(r.reports))
      .catch(() => {});
  }, [domain, isAdmin]);

  if (!isAdmin()) return null;

  const stackedData = series && series.length > 0
    ? series.map(p => ({
        date: p.date,
        Successful: p.successful_sessions,
        Failure: p.failure_sessions,
      }))
    : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button
          component={RouterLink}
          to="/app/admin/tlsrpt"
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          All domains
        </Button>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {domain}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            TLS-RPT — TLS handshake outcomes reported by inbound peers
          </Typography>
        </Box>
        <Box>{summary && <ReadinessBadge summary={summary} />}</Box>
      </Stack>

      <Stack direction="row" spacing={1} mb={2}>
        <ButtonGroup size="small">
          {[7, 14, 30, 60, 90].map(d => (
            <Button
              key={d}
              variant={days === d ? 'contained' : 'outlined'}
              onClick={() => setDays(d)}
            >
              {d}d
            </Button>
          ))}
        </ButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error}</Alert>}

      {/* Summary tiles */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Reports {days}d</Typography>
            <Typography variant="h5" fontWeight={600}>{fmtNumber(summary?.report_count_30d)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Successful sessions</Typography>
            <Typography variant="h5" fontWeight={600}>
              {fmtNumber(summary?.successful_sessions_30d)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Failure sessions</Typography>
            <Typography
              variant="h5"
              fontWeight={600}
              color={summary && summary.failure_sessions_30d > 0 ? 'error.main' : undefined}
            >
              {fmtNumber(summary?.failure_sessions_30d)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Failure rate</Typography>
            <Typography variant="h5" fontWeight={600}>
              {fmtPct(summary?.failure_rate_30d)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Timeseries chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            Daily TLS session outcomes
          </Typography>
          <Box sx={{ height: 280 }}>
            {!series && <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>}
            {series && series.length === 0 && (
              <Typography color="text.secondary" align="center" py={4}>
                No data in this window.
              </Typography>
            )}
            {series && series.length > 0 && (
              <ResponsiveBar
                data={stackedData}
                keys={['Successful', 'Failure']}
                indexBy="date"
                margin={{ top: 10, right: 110, bottom: 50, left: 60 }}
                padding={0.3}
                colors={['#4caf50', '#f44336']}
                axisBottom={{
                  tickRotation: -45,
                  legend: 'date',
                  legendOffset: 42,
                  legendPosition: 'middle',
                }}
                axisLeft={{
                  legend: 'sessions',
                  legendOffset: -50,
                  legendPosition: 'middle',
                }}
                labelSkipWidth={20}
                labelSkipHeight={14}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'right',
                    direction: 'column',
                    translateX: 100,
                    itemWidth: 80,
                    itemHeight: 18,
                    symbolSize: 12,
                  },
                ]}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Failure breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            Top failure types
          </Typography>
          {!failures && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} /></Box>}
          {failures && failures.length === 0 && (
            <Typography color="text.secondary" align="center" py={3}>
              No failures observed in this window. 🎉
            </Typography>
          )}
          {failures && failures.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Result type</TableCell>
                    <TableCell>Receiving MX</TableCell>
                    <TableCell align="right">Failure sessions</TableCell>
                    <TableCell align="right">Sending IPs</TableCell>
                    <TableCell>Sample additional info</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failures.map((f, i) => (
                    <TableRow hover key={`${f.result_type}|${f.receiving_mx_hostname}|${i}`}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.result_type}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.receiving_mx_hostname}</TableCell>
                      <TableCell align="right">{fmtNumber(f.failure_sessions)}</TableCell>
                      <TableCell align="right">{fmtNumber(f.sending_mta_ip_count)}</TableCell>
                      <TableCell
                        sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {f.sample_additional_information || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Reporters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            Reporting receivers
          </Typography>
          {!reporters && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} /></Box>}
          {reporters && reporters.length === 0 && (
            <Typography color="text.secondary" align="center" py={3}>
              No receivers have sent reports yet in this window.
            </Typography>
          )}
          {reporters && reporters.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell align="right">Reports</TableCell>
                    <TableCell align="right">Successful</TableCell>
                    <TableCell align="right">Failure</TableCell>
                    <TableCell align="right">Failure rate</TableCell>
                    <TableCell align="right">Last seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reporters.map(r => (
                    <TableRow hover key={r.organization}>
                      <TableCell sx={{ fontWeight: 500 }}>{r.organization}</TableCell>
                      <TableCell align="right">{fmtNumber(r.reports)}</TableCell>
                      <TableCell align="right">{fmtNumber(r.successful_sessions)}</TableCell>
                      <TableCell align="right" sx={{ color: r.failure_sessions > 0 ? 'error.main' : undefined }}>
                        {fmtNumber(r.failure_sessions)}
                      </TableCell>
                      <TableCell align="right">{fmtPct(r.failure_rate)}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: 12 }}>
                        {fmtEpoch(r.last_seen_epoch)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Recent reports */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={1}>
            Recent reports
          </Typography>
          {!reports && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} /></Box>}
          {reports && reports.length === 0 && (
            <Typography color="text.secondary" align="center" py={3}>
              No reports yet.
            </Typography>
          )}
          {reports && reports.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reporting org</TableCell>
                    <TableCell>Window</TableCell>
                    <TableCell align="right">Successful</TableCell>
                    <TableCell align="right">Failure</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map(rpt => (
                    <TableRow hover key={rpt.report_uid}>
                      <TableCell>{rpt.organization}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {fmtEpoch(rpt.date_begin)} → {fmtEpoch(rpt.date_end)}
                      </TableCell>
                      <TableCell align="right">{fmtNumber(rpt.total_successful)}</TableCell>
                      <TableCell align="right" sx={{ color: rpt.total_failure > 0 ? 'error.main' : undefined }}>
                        {fmtNumber(rpt.total_failure)}
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/app/admin/tlsrpt/domains/${encodeURIComponent(domain)}/reports/${encodeURIComponent(rpt.report_uid)}`}
                          underline="hover"
                        >
                          view
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TlsRptDomainDetail;
